import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";
import _, { toPlainObject } from 'lodash';
import { AccountManagementEvent } from "@/services/analytics/events";
import { InteractionTrackingType } from "@/services/analytics/platforms/adobe-analytics/models";
import { SubmitOrderRequestPaymentMethodType, TokenizedPaymentCard } from "@/models";

interface UComConfig {
  accessToken: string;
  apiKey: string;
  pageUrl: string;
  mountId: string;
  encryptionKey: string;
  redirectUrl: string;
  debug?: boolean;
}

interface UComNonce {
  token: UComNonceToken;
}

interface UComNonceToken {
  tokenId: string;
}

@Component({
  name: 'CmgUComForm',
})

export default class CmgUComForm extends Vue {
  // #region Public Properties
  public isPaymentProviderInitializing = true;
  public errorMessages: string[] = [];

  @Prop({ required: true, type: String })
  public providerId!: string;

  @Prop({ required: true, type: Boolean })
  checkout!: boolean;

  @Prop({ required: true, type: Boolean })
  public oneTimePayment!: boolean;
  // #endregion

  // #region Private Fields
  private tokenId!: string;
  private apiKey!: string;
  private encryptionKey!: string;
  private pageUrl!: string;
  private redirectUrl!: string;

  private nonceToken!: string;

  isSubmitted: boolean = false;
  isFormValid: boolean = false;
  submitText: string = !this.oneTimePayment ? 'Save card' : 'Add Payment';
  // #endregion  

  // #region Ctor
  constructor() {
    super();
  }
  // #endregion

  // #region Public Functions
  public async mounted(): Promise<void> {
    try {
      this.$services.payment.loadUComJsLibrary().then(async () => {
        this.getSessionToken()
          .then(() => {
            //Configuration object
            const configs: UComConfig = {
              accessToken: this.tokenId,
              apiKey: this.apiKey,
              pageUrl: this.pageUrl,
              mountId: "ucom-container",
              encryptionKey: this.encryptionKey,
              redirectUrl: encodeURI(this.redirectUrl)
            };

            //  /*Below attribute should be enabled when you integrate and test it on 
            //  http://localhist in lower (CAT) environment. This attribute should be removed in 
            //  higher environment.  Passing in false doesn't work, needs to be commented out / removed completely*/
            if (this.$services.config.getUComIsDebugConfiguration() === true) {
              configs.debug = true;
            }

            (window as any).ucomSDK.init(configs, this.returnCallback, true);
            (window as any).ucomSDK.on('ready', this.onReady);
            (window as any).ucomSDK.on('change', this.onChange);
            (window as any).ucomSDK.on('error', this.onError);
            (window as any).ucomSDK.on('success', this.onSuccess);
            (window as any).ucomSDK.start();
          })
          .catch(error => {
            this.$services.logger.error('An error was encountered while initializing the form', error);
            this.$services.ui.setModalVisibility('globalErrorToast', true);
          });
      });
    } catch (e: any) {
      this.errorMessages.push('Oops an error was encontered while fetching a session token from the service. Please try again!');
    }
  }

  public async saveCreditCard(e: Event): Promise<void> {
    e.preventDefault();

    try {
      if (this.providerId && this.isFormValid) {
        this.isSubmitted = true;
        (window as any).ucomSDK.triggerSaveAction();
      }
    } catch (error: any) {
      this.$services.logger.error('An error was encountered while submitting the form', error);
      this.isFormValid = false;
      this.closeModal();
      this.$services.ui.setModalVisibility('globalErrorToast', true);
    }

    this.$services.analytics.send(new AccountManagementEvent(InteractionTrackingType.AddCreditCard));
  }

  get showErrors() {
    return this.errorMessages.length != 0;
  }
  // #endregion

  // #region Private Functions
  private async getSessionToken(): Promise<void> {
    if (!this.providerId) {
      throw new Error('A valid provider is required');
    }

    try {
      const initResponse = await this.$services.transaction.initializeRequest(this.providerId);
      if (_.isUndefined(initResponse.ucom?.tokenID) || _.isUndefined(initResponse.ucom?.apiKey) ||
        _.isUndefined(initResponse.ucom?.encryptionKey) || _.isUndefined(initResponse.ucom?.pageUrl) ||
        _.isUndefined(initResponse.ucom?.redirectUrl)) {
        throw Error('No request data returned by the service');
      } else {
        this.tokenId = initResponse.ucom!.tokenID;
        this.apiKey = initResponse.ucom!.apiKey;
        this.encryptionKey = initResponse.ucom!.encryptionKey;
        this.pageUrl = initResponse.ucom!.pageUrl;
        this.redirectUrl = initResponse.ucom!.redirectUrl;
      }
    } catch (error: any) {
      this.$services.logger.error('An error was encountered while retrieving a session token from server.', error);
      throw new Error('The request session could not be fulfilled');
    } finally {
      this.isPaymentProviderInitializing = false;
    }
  }

  private closeModal(): void {
    const args = this.$appStore.state.ui.modals.addCreditOrDebitCardModal.args;
    if (args && args.onClose) {
      args.onClose();
    }
    this.$services.ui.setModalVisibility('addCreditOrDebitCardModal', false);
    const focusNearby = document.getElementsByClassName('pay-total')[0] as HTMLElement;
    if (focusNearby) {
      focusNearby.focus();
    }
    Vue.nextTick(() => {
      setTimeout(() => {
        const focusaddAnothercard = document.getElementsByClassName('addAnothercard')[0] as HTMLElement;
        if (focusaddAnothercard) {
          focusaddAnothercard.focus();
          this.$services.ui.clearLastSelectedElement();
        } else {
          const focusaddGiftcard = document.getElementsByClassName('gift-card-section-header')[0] as HTMLElement;
          if (focusaddGiftcard) {
            focusaddGiftcard.focus();
            this.$services.ui.clearLastSelectedElement();
          }
        }
      }, 2000);
    });
  }

  private async completePayment(): Promise<void> {
    this.isPaymentProviderInitializing = false;
    if (this.providerId) {
      try {
        const tokenizedCardResponse = await this.$services.transaction.getTransactionToken(this.providerId, this.nonceToken, this.tokenId);
        const tokenizedCard = {
          cardHolderName: tokenizedCardResponse.cardHolderName,
          expiration: tokenizedCardResponse.expiration,
          paymentMethod: tokenizedCardResponse.paymentMethod,
          tokenizedAccountNumber: tokenizedCardResponse.paymentToken,
          paymentProviderId: this.providerId,
          lastFourAccountNumbers: tokenizedCardResponse.last4,
          billingZip: tokenizedCardResponse.billingZip
        } as TokenizedPaymentCard;

        // if we landed here from one time payment modal, we want to just set the payment method
        // else, just saving the card
        if (this.oneTimePayment) {
          this.setOneTimePaymentMethod(tokenizedCard);
        } else {
          await this.savePaymentCard(tokenizedCard);
        }
      } catch (err: any) {
        this.$services.logger.error('An error was encountered while retrieving the tokenized card', err);
        this.$services.ui.setModalVisibility('globalErrorToast', true);
      } finally {
        this.closeModal();
      }
    }
  }

  private setOneTimePaymentMethod(tokenizedCard: TokenizedPaymentCard) {
    // Mark card as selected
    this.$services.checkout.setPaymentMethods([
      {
        cardHolderName: tokenizedCard.cardHolderName,
        creditCardSingleUseToken: tokenizedCard.tokenizedAccountNumber,
        creditCardType: tokenizedCard.paymentMethod.replace(/-/g, ' '),
        creditCardExpiration: tokenizedCard.expiration,
        creditCardZipCode: tokenizedCard.billingZip,
        paymentType: SubmitOrderRequestPaymentMethodType.CreditCard,
        paymentProviderId: this.providerId,
        lastFourAccountNumbers: tokenizedCard.lastFourAccountNumbers,
      },
    ]);

    // Include card in card list as one-time-payment
    if (tokenizedCard.expiration) {
      tokenizedCard.expirationMonth = tokenizedCard.expiration.substr(0, 2);
      tokenizedCard.expirationYear = tokenizedCard.expiration.substr(2, 2);
    }
    tokenizedCard.paymentMethod = tokenizedCard.paymentMethod.toLowerCase();
    tokenizedCard.isOneTimePaymentCard = true;
    this.$services.checkout.setOneTimePayCard(tokenizedCard);
  }

  private async savePaymentCard(tokenizedCard: any): Promise<void> {
    await this.$services.transaction
      .savePaymentCard(tokenizedCard)
      .then(() => {
        this.$services.transaction.refreshPaymentCards();
        this.closeModal();
      })
      .catch(() => {
        this.errorMessages.length = 0;
        this.errorMessages.push('Could not save credit card. Please try again later.');
      });
  }

  // #region UCom callbacks
  // return Callback method
  // Hosted Pages Response (Error/Success) will be returned to this callback 
  //once the hosted page actions get completed.
  private returnCallback(response: any): void {
    /* Kill the uCom UI */
    (window as any).ucomSDK.stop();
    /* Do rest of the flow here */
  }

  private onReady() {
  }

  private onChange(event: any) {
    this.isFormValid = event.formValid;
  }

  private onError(response: any) {
    this.isSubmitted = false;
    this.closeModal();
    this.$services.logger.error(`An error was encoutered trying to tokenize the card: ${response}`);
    this.$services.ui.setModalVisibility('globalErrorToast', true);
  }

  private onSuccess(response: any) {
    const nonce: UComNonce = JSON.parse(response);
    this.nonceToken = nonce.token.tokenId;
    this.completePayment();
  }
  // #endregion

  // #endregion
}